import type { JariMasukan, LobusKey } from "@dfa/types";

export type HuluHasil = {
  TNGF: number;
  jariX: string[];
  lobus_nilai: Record<LobusKey, number>;
  lobus_persen: Record<LobusKey, number>;
  lobus_X: LobusKey[];
};

const LOBUS_BY_FINGER = ["Pre", "Fro", "Par", "Tem", "Occ"] as const;

const ARCH = new Set([
  "SIMPLE ARCH",
  "TENTED ARCH",
  "ARCH WITH ULNAR",
  "ARCH WITH RADIAL",
  "ENCLOSED ARCH",
  "ENCLOSED",
]);

const WHORL = new Set([
  "SPIRAL WHORL",
  "TARGET WHORL",
  "ELONGATED WHORL",
  "ELONGATED",
  "COMPOSITE",
  "IMPLODING WHORL",
  "DOUBLE LOOP",
  "ACCIDENTAL",
  "ACCIDENTALS",
]);

const LOOP = new Set([
  "ULNAR LOOP",
  "RADIAL LOOP",
  "FALLING LOOP",
  "CENTRAL POCKET",
  "LATERAL POCKET",
]);

const TAK_TERBACA = new Set(["MALFORMATION"]);

export type Keluarga = "arch" | "loop" | "whorl" | "tak_terbaca";

function polaNorm(pola: string): string {
  return pola.trim().toUpperCase();
}

/** §A — keluarga eksplisit; pola tak dikenal melempar, bukan jadi loop. */
export function keluarga(pola: string): Keluarga {
  const p = polaNorm(pola);
  if (ARCH.has(p)) return "arch";
  if (WHORL.has(p)) return "whorl";
  if (LOOP.has(p)) return "loop";
  if (TAK_TERBACA.has(p)) return "tak_terbaca";
  throw new Error(`pola tidak dikenal: "${pola}" — kosakata tidak dipetakan?`);
}

function ridgeCount(jari: JariMasukan): number {
  const fam = keluarga(jari.pola);
  if (fam === "arch" || fam === "tak_terbaca") return 0;
  if (fam === "whorl") return Math.max(jari.radial, jari.ulnar);
  return jari.radial || jari.ulnar;
}

export function kodeKeLobus(kode: string): LobusKey {
  const tangan = kode[0];
  const nomor = Number(kode.slice(1));
  if ((tangan !== "R" && tangan !== "L") || nomor < 1 || nomor > 5) {
    throw new Error(`kode jari tidak valid: ${kode}`);
  }
  const hem = tangan === "R" ? "K" : "N";
  return `${hem}${LOBUS_BY_FINGER[nomor - 1]}` as LobusKey;
}

function lobusKosong(): Record<LobusKey, number> {
  return {
    KPre: 0,
    KFro: 0,
    KPar: 0,
    KTem: 0,
    KOcc: 0,
    NPre: 0,
    NFro: 0,
    NPar: 0,
    NTem: 0,
    NOcc: 0,
  };
}

/** §A — Holt 1968: jari → 10 lobus, TNGF bilangan bulat. */
export function hitungHulu(jari: JariMasukan[]): HuluHasil {
  if (jari.length !== 10) {
    throw new Error(`butuh tepat 10 jari, diberi ${jari.length}`);
  }

  const lobus_nilai = lobusKosong();
  const jariX: string[] = [];
  const lobus_X = new Set<LobusKey>();

  for (const j of jari) {
    const slot = kodeKeLobus(j.kode);
    if (lobus_nilai[slot] !== undefined && jari.some((x) => x.kode !== j.kode && kodeKeLobus(x.kode) === slot)) {
      throw new Error(`slot lobus kembar: ${slot}`);
    }
    const rc = ridgeCount(j);
    lobus_nilai[slot] = rc;
    if (keluarga(j.pola) === "arch" || keluarga(j.pola) === "tak_terbaca") {
      jariX.push(j.kode);
      lobus_X.add(slot);
    }
  }

  const TNGF = jari.reduce((s, j) => s + ridgeCount(j), 0);
  if (TNGF === 0) {
    throw new Error("TNGF nol — seluruh jari ber-arch");
  }

  const lobus_persen = lobusKosong();
  for (const key of Object.keys(lobus_nilai) as LobusKey[]) {
    lobus_persen[key] = (lobus_nilai[key] / TNGF) * 100;
  }

  return {
    TNGF,
    jariX,
    lobus_nilai,
    lobus_persen,
    lobus_X: [...lobus_X].sort(),
  };
}
